import { FlowType } from '@cash-web/protos/squareup/cash/plasma/flows/FlowProto.pb'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'

import { InternalRouteAction, InternalRouteMatch } from './types'
import { redirect } from './utils'

// order matters, first match wins if there are multiple matches
export const internalRouteHandlers: InternalRouteAction[] = [
  {
    match: ['https://internal.cash.app/dl/view/taxes/hub'],
    handler: () => redirect({ url: '/taxes', target: '_blank' }),
  },
  {
    match: [
      'https://internal.cash.app/dl/view/address',
      'https://internal.cash.app/dl/view/edit/profile',
      'https://internal.cash.app/dl/view/profile',
    ],
    handler: () => redirect({ app: 'account', url: '/settings', target: '_self' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/activity'],
    handler: () => redirect({ app: 'account', url: '/activity', target: '_self' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/profiles/switcher'],
    handler: () => redirect({ app: 'account', url: '/settings/switch-account', target: '_self' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/card'],
    handler: () => redirect({ app: 'account', url: '/card', target: '_self' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/documents'],
    handler: () => redirect({ app: 'account', url: '/documents', target: '_self' }),
  },
  {
    match: [
      'https://internal.cash.app/dl/view/payment-pad',
      /https:\/\/internal\.cash\.app\/dl\/view\/pay\/[^/]+\/[0-9.]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/pay\/[^/]+\/[0-9.]+\/note\/[^/]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/pay-sms-v2\/[^/]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/pay-email-v2\/[^/]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/request\/[^/]+\/[0-9.]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/request\/[^/]+\/[0-9.]+\/note\/[^/]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/request-sms\/[^/]+/,
      /https:\/\/internal\.cash\.app\/dl\/view\/request-email\/[^/]+/,
    ],
    handler: () => redirect({ app: 'account', url: '/pay-and-request', target: '_self' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/support-home', 'https://internal.cash.app/dl/view/support'],
    handler: () => redirect({ app: 'support', url: '', target: '_blank' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/balance', 'https://internal.cash.app/dl/view/add-cash'],
    handler: () => redirect({ app: 'account', url: '/money', target: '_self' }),
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/view\/add-cash\/(\d+)/],
    handler: (url: string, match: InternalRouteMatch) => {
      const amountCents = parseInt(url.split('/add-cash/')[1])
      return {
        type: 'journey',
        journey: `/dl/view/add-cash/${amountCents}`,
      }
    },
  },
  {
    match: ['https://internal.cash.app/dl/view/balance/transfers-setup'],
    handler: () => ({ type: 'journey', journey: `/dl/flow/${FlowType.POSTBANK_RETRIEVE_BANKING_INFORMATION}` }),
  },
  {
    match: ['https://internal.cash.app/dl/view/bitcoin/buy'],
    handler: () => ({ type: 'journey', journey: `/dl/view/bitcoin/buy` }),
  },
  {
    match: ['https://internal.cash.app/dl/view/paychecks/distribution-summary'],
    handler: () => {
      return {
        type: 'journey',
        journey: `/dl/view/paychecks/distribution-summary`,
      }
    },
  },
  {
    match: ['https://internal.cash.app/dl/view/investing/round_ups'],
    handler: () => {
      return {
        type: 'journey',
        journey: `/dl/flow/${FlowType.PIGGYBANK_SAVINGS_ROUND_UP}`,
      }
    },
  },
  {
    match: ['https://internal.cash.app/dl/view/balance/direct-deposit-setup'],
    handler: () => {
      return {
        type: 'journey',
        journey: `/dl/flow/${FlowType.GET_DIRECT_DEPOSIT_FORM}`,
      }
    },
  },
  {
    match: ['https://internal.cash.app/dl/view/profile-personal'],
    handler: () => redirect({ app: 'account', url: '/settings', target: '_self' }),
  },
  // Activity items routes that are intentially hidden due to risk of abuse or other reasons
  {
    match: [
      /INITIATE_CLAIM/,
      /P2P_REPORT_SCAM/,
      /INITIATE_DISPUTE_CLAIM/,
      /CREATE_OR_EDIT_RECURRING_PAYMENT/,
      /VIEW_PAYMENT_PLAN_FLOW/,
      /CLOSE_MONEY_POOL/,
      /BORROW_ONBOARDING/,
      /https:\/\/internal.cash.app\/dl\/view\/profile\/customer\/[^/]+/,
      /https:\/\/internal.cash.app\/dl\/view\/savings_with_modal_half_sheet\?name=get-cash-card-to-start-earning/,
      /https:\/\/internal.cash.app\/dl\/view\/investing\/round_ups_failed_confirmation/, // https://block.atlassian.net/browse/FSWEB-11133
      /https:\/\/internal.cash.app\/dl\/view\/investing\/roundup_aggregated_transactions/, // https://block.atlassian.net/browse/FSWEB-11135
      /https:\/\/internal.cash.app\/dl\/view\/investing\/round_ups_skipped_confirmation/, // https://block.atlassian.net/browse/FSWEB-11137
      /https:\/\/internal.cash.app\/dl\/view\/card-recurring-payments\/info-sheet/, // https://block.atlassian.net/browse/FSWEB-11138
      /https:\/\/internal.cash.app\/dl\/flow\/VIEW_PAYMENT_PLAN_FLOW/, // https://block.atlassian.net/browse/CASHW-3741
      /https:\/\/internal.cash.app\/dl\/flow\/BUSINESS_ACCOUNT_CREATION/, // https://block.atlassian.net/browse/CASHW-3742
      new RegExp(FlowType.VIEW_AP_PAYMENT_PLAN_FLOW), // not yet implemented on web
    ],
    handler: (url: string, match: InternalRouteMatch) => {
      // intentionally hiding these due to risk of abuse or other reasons
      return {
        type: 'known-unsupported',
        url,
      }
    },
  },
  // Show these items that have these routes on the activity page but direct people to the mobile app
  {
    match: [/VIEW_MONEY_POOL/, /https:\/\/internal.cash.app\/dl\/view\/pools\/.*\/ACTIVITY/],
    handler: () => ({ type: 'journey', journey: '/web/view/activity/not-supported' }),
  },
  {
    match: ['https://internal.cash.app/dl/view/request-cash-card/style-picker'],
    handler: () => ({ type: 'journey', journey: `/dl/flow/type/${FlowType.REQUEST_PHYSICAL_CARD}` }),
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/view\/support\/[^/]+/],
    handler: (url: string) => {
      const matches = url.match(/\/support\/([^/]+)/)
      const supportTopic = matches?.[1] ?? ''

      return redirect({ app: 'support', url: `/${supportTopic}`, target: '_blank' })
    },
  },
  {
    match: ['https://internal.cash.app/web/view/reload'],
    handler: () => ({
      type: 'internal',
      redirect: () => window.location.reload(),
    }),
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/view\/activity\/payment\/.*/],
    handler: (url: string) => {
      const activityItemId = url.split('/payment/')[1]
      const queryString = window.location.search ?? ''
      return redirect({
        app: 'account',
        url: `/activity/${activityItemId}${queryString}`,
        target: '_self',
        journeyFallback: false,
      })
    },
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/view\/pools\/.*/],
    handler: (url: string) => {
      return {
        type: 'known-unsupported',
        url,
      }
    },
  },
  {
    match: [new RegExp('https://internal.cash.app/dl/view/savings/goal/([^/]+)')],
    handler: (url: string, match: InternalRouteMatch) => {
      const goalToken = url.split('/goal/')[1]
      return {
        type: 'journey',
        journey: `/dl/view/savings/goal/${goalToken}`,
      }
    },
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/view\/savings\/home/],
    handler: () => {
      return redirect({ app: 'account', url: '/savings', target: '_self' })
    },
  },
  {
    match: ['https://internal.cash.app/dl/view/investing/round_ups_onboarding'],
    handler: () => {
      return {
        type: 'journey',
        journey: '/dl/view/investing/round_ups_onboarding',
      }
    },
  },
  {
    match: ['https://internal.cash.app/dl/background/update-afterpay-applet?blocking=true'],
    handler: (url: string) => {
      // Route to perform a background request and to optionally clear cached data, updating all data associated with the Afterpay Applet
      // This is a known unsupported route, since we invalidate queries in GetFlow.tsx
      return {
        type: 'known-unsupported',
        url: url,
      }
    },
  },
  {
    match: [/https:\/\/cash[^.]*.app\/f\//, /https:\/\/internal.cash.app\/dl\/flow\//],
    handler: (url: string, match: InternalRouteMatch) => {
      const denyList = ['authentication']
      const decodedUrl = decodeURIComponent(url)
      const [flowPath, queryString = ''] = decodedUrl.replace(match, '').split('?')

      // Split into individual params and filter out denied ones
      const filteredParams = queryString
        .split('&')
        .filter(param => {
          const [key] = param.split('=')
          return !denyList.includes(key)
        })
        .join('&')

      // Construct the initiationData with allowed parameters
      const initiationData = filteredParams ? `${flowPath}?${filteredParams}` : flowPath
      return {
        type: 'journey',
        journey: `/dl/flow/${initiationData}`,
        replace: false,
      }
    },
  },
  {
    match: [/https:\/\/internal.cash.app\/dl\/scenario\//],
    handler: (url: string, match: InternalRouteMatch) => {
      const clientScenario = decodeURIComponent(url).replace(match, '') as ClientScenario
      return {
        type: 'journey',
        journey: `/dl/scenario/${clientScenario}`,
        replace: false,
      }
    },
  },
  {
    match: [/.+\/payments\/ST\$_BTCWDL_([^/]+)\/receipt/g],
    handler: (url: string, match: InternalRouteMatch) => {
      const newUrl = url.replace(match, '/receipt/btc_withdrawal/$1')
      return redirect({ url: newUrl, target: '_blank' })
    },
  },
  {
    match: [/.+(\/receipt\/btc_deposit.+)/],
    handler: (url: string, match: InternalRouteMatch) => {
      const newUrl = url.replace(match, '$1')
      return redirect({ url: newUrl, target: '_blank' })
    },
  },
  {
    // foresight is an internal testing tool which would get picked up by the generic unsupported internal route handler
    // but actually should be handled by the external link handler
    match: [/https:\/\/internal.cash.app\/foresight/],
    handler: () => undefined,
  },
  // Generic unsupported internal route handler (if it's not matched by any of the above handlers)
  {
    match: [/https:\/\/internal.cash.app\//],
    handler: (url: string) => ({
      type: 'unsupported',
      url,
    }),
  },
]
